import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '../../components/Button'
import type { AppEvent } from '../../store/eventsSlice'
import { loadGoogleFont, CURATED_FONTS, isRegistrationOpen } from '../../utils/event'
import { formatEmail, isValidEmail } from '../../utils/email'
import { normaliseLanguage } from '../../utils/downloadParticipantReport'

/** Values collected by {@link RegistrationFormView} on submit. */
export interface RegistrationFormValues {
  firstName: string
  lastName: string
  email: string
  companyName: string
  consentDataCollection: boolean
  consentMediaRecording: boolean
  /** Registrant's current UI language (e.g. "ja"), so backend emails match it. */
  language: string
}

/** Props for {@link RegistrationFormView}. */
export interface RegistrationFormViewProps {
  event: AppEvent
  /**
   * Renders inside the event editor's live preview pane instead of the real public
   * `/e/:id/register` route: typing and the "closed"/"open" states still reflect the
   * draft event exactly as a visitor would see them, but submitting never calls the real
   * registration API — it simulates the success screen so organisers can see the entire
   * flow (including what a confirmed registration looks like) without creating fake data.
   */
  previewMode?: boolean
  /** Prefilled email from the `?email=` query param (invite links). Ignored in `previewMode`. */
  prefillEmail?: string
  /**
   * Remaining prefills carried by a rejection email's "update & resubmit" link (see #215), so a
   * rejected applicant doesn't retype details already on file. The consent prefills only ever
   * pre-*check* a box: a consent that was never given still requires an explicit, active choice.
   */
  prefillFirstName?: string
  prefillLastName?: string
  prefillCompany?: string
  prefillConsentPersonal?: boolean
  prefillConsentMedia?: boolean
  /**
   * Performs the real registration API call. Required unless `previewMode`. On failure this
   * must throw an `Error` whose `message` is already a translated, user-facing string — the
   * view displays it as-is without knowing anything about API error codes.
   */
  onSubmit?: (values: RegistrationFormValues) => Promise<void>
  /** Target for the "Back to event" link. The link is omitted entirely when this is absent
   * (e.g. in `previewMode`, where there is no real event page to go back to). */
  backHref?: string
}

/**
 * Presentational rendering of the public registration form. Shared by {@link RegisterPage}
 * (the real `/e/:id/register` route, backed by the registration API) and the event editor's
 * live preview pane, so organisers see exactly what visitors will fill in — including field
 * order, placeholders, required markers and the closed/success states — before publishing.
 */
export function RegistrationFormView({
  event,
  previewMode = false,
  prefillEmail = '',
  prefillFirstName = '',
  prefillLastName = '',
  prefillCompany = '',
  prefillConsentPersonal = false,
  prefillConsentMedia = false,
  onSubmit,
  backHref,
}: RegistrationFormViewProps) {
  const { t, i18n } = useTranslation()
  const b = event.branding

  const [firstName, setFirstName] = useState(prefillFirstName)
  const [lastName, setLastName] = useState(prefillLastName)
  const [email, setEmail] = useState(prefillEmail)
  const [company, setCompany] = useState(prefillCompany)
  const [consentPersonal, setConsentPersonal] = useState(prefillConsentPersonal)
  const [consentMedia, setConsentMedia] = useState(prefillConsentMedia)
  const [emailTouched, setEmailTouched] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const regOpen = isRegistrationOpen(event)

  const emailValid = isValidEmail(email)
  const showEmailError = emailTouched && email.length > 0 && !emailValid
  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    emailValid &&
    company.trim().length > 0 &&
    consentPersonal

  useEffect(() => {
    loadGoogleFont(b.headingFont)
    loadGoogleFont(b.bodyFont)
  }, [b.headingFont, b.bodyFont])

  const headingFamily = b.headingFont
    ? `'${b.headingFont}', ${CURATED_FONTS.find((f) => f.family === b.headingFont)?.family ?? 'serif'}`
    : 'serif'
  const bodyFamily = b.bodyFont ? `'${b.bodyFont}', sans-serif` : 'sans-serif'

  const cssVars = {
    '--ev-bg': b.background,
    '--ev-text': b.text,
    '--ev-primary': b.primary,
    '--ev-accent': b.accent,
    '--ev-heading-font': headingFamily,
    '--ev-body-font': bodyFamily,
  } as React.CSSProperties

  const inputStyle: React.CSSProperties = {
    background: `${b.primary}0D`,
    border: `1px solid ${b.primary}24`,
    color: b.text,
    fontFamily: bodyFamily,
  }

  const inputCls =
    'w-full rounded-xl px-4 py-3 text-sm outline-none transition-shadow placeholder:opacity-35 focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--ev-accent)_28%,transparent)]'
  const labelCls = 'text-[11px] font-semibold tracking-[0.12em] uppercase opacity-55'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setEmailTouched(true)
    if (!canSubmit) return

    setSubmitError(null)
    // In previewMode there's nothing to save — jump straight to the success screen so
    // organisers can review that state too, without a real registration ever being created.
    if (previewMode) {
      setSubmitted(true)
      return
    }
    if (!onSubmit) return
    try {
      await onSubmit({
        email: formatEmail(email),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyName: company.trim(),
        consentDataCollection: consentPersonal,
        consentMediaRecording: consentMedia,
        language: normaliseLanguage(i18n.language),
      })
      setSubmitted(true)
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : t('reg_err_generic'))
    }
  }

  return (
    <div
      style={{
        ...cssVars,
        background: 'var(--ev-bg)',
        color: 'var(--ev-text)',
        fontFamily: 'var(--ev-body-font)',
      }}
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 py-10 sm:px-6"
    >
      {/* Atmospheric background washes built from the event palette */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(60rem 40rem at 12% -10%, ${b.accent}26, transparent 60%), radial-gradient(50rem 40rem at 110% 110%, ${b.primary}26, transparent 55%)`,
        }}
      />

      <div
        className="relative grid w-full max-w-4xl overflow-hidden rounded-3xl shadow-2xl md:grid-cols-[0.82fr_1fr]"
        style={{ background: b.background, border: `1px solid ${b.primary}24` }}
      >
        {/* ── Hero panel ─────────────────────────────────────────────────────── */}
        <aside
          className="relative flex flex-col justify-between gap-10 overflow-hidden p-8 sm:p-10"
          style={{
            background: `linear-gradient(155deg, ${b.primary}1F, ${b.accent}14 55%, transparent)`,
            borderRight: `1px solid ${b.primary}1A`,
          }}
        >
          {/* Decorative oversized glyph */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-10 -right-6 text-[11rem] leading-none font-black select-none"
            style={{ color: b.accent, opacity: 0.08, fontFamily: headingFamily }}
          >
            ✦
          </div>

          {/* Centered on mobile (logo/label/title read better stacked and centered when the
              hero panel spans the full width above the form); reverts to left-aligned once the
              two-column layout kicks in at md, matching the classic hero-panel look. */}
          <div className="relative flex flex-col items-center gap-6 text-center md:items-start md:text-left">
            {/* Logo — omitted entirely when the event has none, matching PublicEventPage */}
            {b.logo && (
              <img
                src={b.logo.dataUrl}
                alt={event.title}
                className="h-12 max-w-[160px] object-contain"
              />
            )}

            <div className="flex flex-col gap-2">
              <span
                className="text-[11px] font-semibold tracking-[0.28em] uppercase"
                style={{ color: b.accent }}
              >
                {t('pep_registration_label')}
              </span>
              <h1
                className="text-3xl leading-[1.05] font-black tracking-tight sm:text-4xl"
                style={{ fontFamily: headingFamily }}
              >
                {event.title}
              </h1>
            </div>
          </div>

          {/* Reassurance footer */}
          <div className="relative flex flex-col gap-3 text-sm opacity-70">
            <div className="flex items-center gap-2.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: b.accent }}
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>{t('reg_security_data')}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: b.accent }}
                aria-hidden="true"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>{t('reg_security_email')}</span>
            </div>
          </div>
        </aside>

        {/* ── Form panel ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-7 p-8 sm:p-10">
          {/* Registration closed */}
          {!regOpen ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: b.primary, opacity: 0.5 }}
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p className="text-lg font-semibold opacity-70" style={{ fontFamily: headingFamily }}>
                {t('pep_registration_closed')}
              </p>
              <p className="max-w-xs text-sm opacity-45">{t('reg_closed_desc')}</p>
              {backHref && (
                <Link
                  to={backHref}
                  className="mt-2 text-xs opacity-40 transition-opacity hover:opacity-70"
                >
                  {t('reg_back_to_event')}
                </Link>
              )}
            </div>
          ) : !submitted ? (
            /* Form */
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-5" noValidate>
              {/* Last Name + First Name side by side */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="reg-last-name" className={labelCls}>
                    {t('reg_form_last_name')} <span style={{ color: b.accent }}>*</span>
                  </label>
                  <input
                    id="reg-last-name"
                    required
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('reg_placeholder_last_name')}
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="reg-first-name" className={labelCls}>
                    {t('reg_form_first_name')} <span style={{ color: b.accent }}>*</span>
                  </label>
                  <input
                    id="reg-first-name"
                    required
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('reg_placeholder_first_name')}
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="reg-email" className={labelCls}>
                  {t('reg_form_email')} <span style={{ color: b.accent }}>*</span>
                </label>
                <input
                  id="reg-email"
                  required
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder={t('reg_placeholder_email')}
                  aria-invalid={showEmailError}
                  aria-describedby={showEmailError ? 'reg-email-error' : undefined}
                  className={inputCls}
                  style={{
                    ...inputStyle,
                    border: showEmailError ? '1px solid #ef4444' : inputStyle.border,
                  }}
                />
                {showEmailError && (
                  <p id="reg-email-error" className="text-xs font-medium text-red-400">
                    {t('reg_err_email')}
                  </p>
                )}
              </div>

              {/* Company */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="reg-company" className={labelCls}>
                  {t('reg_form_company')} <span style={{ color: b.accent }}>*</span>
                </label>
                <input
                  id="reg-company"
                  required
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={t('reg_placeholder_company')}
                  className={inputCls}
                  style={inputStyle}
                />
              </div>

              {/* Divider before consents */}
              <div className="h-px w-full opacity-10" style={{ background: b.primary }} />

              {/* Consents */}
              <div className="flex flex-col gap-3.5">
                {/* Consent — personal data (required) */}
                <label
                  htmlFor="reg-consent-personal"
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-3 transition-colors"
                  style={{ background: `${b.primary}08` }}
                >
                  <input
                    id="reg-consent-personal"
                    type="checkbox"
                    checked={consentPersonal}
                    onChange={(e) => setConsentPersonal(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[var(--ev-accent)]"
                  />
                  <span className="text-[13px] leading-relaxed opacity-75">
                    {t('reg_consent_personal_text')} <span style={{ color: b.accent }}>*</span>
                  </span>
                </label>

                {/* Consent — media recording (optional) */}
                <label
                  htmlFor="reg-consent-media"
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-3 transition-colors"
                  style={{ background: `${b.primary}08` }}
                >
                  <input
                    id="reg-consent-media"
                    type="checkbox"
                    checked={consentMedia}
                    onChange={(e) => setConsentMedia(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[var(--ev-accent)]"
                  />
                  <span className="text-[13px] leading-relaxed opacity-75">
                    {t('reg_consent_media_text')}
                  </span>
                </label>
              </div>

              {submitError && (
                <p
                  role="alert"
                  className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-500"
                >
                  {submitError}
                </p>
              )}

              <Button
                type="submit"
                variant="unstyled"
                disabled={!canSubmit}
                className="mt-1 rounded-xl px-4 py-3.5 text-sm font-bold tracking-wide transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
                style={{
                  background: b.accent,
                  color: b.background,
                  fontFamily: headingFamily,
                }}
              >
                {t('reg_submit_btn')}
              </Button>

              {/* Back link — omitted when there's no real event page to return to (preview) */}
              {backHref && (
                <Link
                  to={backHref}
                  className="text-center text-xs opacity-40 transition-opacity hover:opacity-70"
                >
                  {t('reg_back_to_event')}
                </Link>
              )}
            </form>
          ) : (
            /* Success state */
            <div className="flex flex-1 flex-col items-center justify-center gap-4 py-10 text-center">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full"
                style={{ background: `${b.accent}1F` }}
              >
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: b.accent }}
                  aria-hidden="true"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2 className="text-2xl font-black" style={{ fontFamily: headingFamily }}>
                {t('reg_success_title')}
              </h2>
              <p className="w-full max-w-sm text-left text-sm opacity-60">
                {t('reg_success_body')}
              </p>
              {backHref && (
                <Link
                  to={backHref}
                  className="mt-2 text-xs opacity-40 transition-opacity hover:opacity-70"
                >
                  {t('reg_back_to_event')}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
